import { useEffect, useState } from "react";
import { flushSync } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import BootstrapModal from "bootstrap/js/dist/modal";
import ZoomModal from "../../components/ZoomModal/ZoomModal";
import MobileZoomModal from "../../components/MobileZoomModal/MobileZoomModal";
import CompareZoomModal from "../../components/CompareZoomModal/CompareZoomModal";
import InternalPagination from "../../components/InternalPagination/InternalPagination";
import { saveHeaderTitle } from "../../features/HomePage/HomePageSlice";
import OptimizedImage from "../../components/OptimizedImage/OptimizedImage";
import { endpoint } from "../../api/endpoint";
import { apiHandler } from "../../api";
import { useIsMobile } from "../../customHook/common/useIsMobile";
import {
  MdCompare,
  MdHourglassEmpty,
  MdInfoOutline,
} from "react-icons/md";

const ComparisonTable = () => {
  const dispatch = useDispatch();
  const token = useSelector((state) => state.auth.token);
  const isMobile = useIsMobile();

  const [zoomModal, setZoomModal] = useState({ front: "", isOpen: false });
  const [initialCompareIndex, setInitialCompareIndex] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [comparisonData, setComparisonData] = useState([]);
  const [averageData, setAverageData] = useState({});
  const [totalRows, setTotalRows] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    dispatch(saveHeaderTitle("Comparison"));
  }, [dispatch]);

  // Fetch comparison results from API
  useEffect(() => {
    if (!token) return;

    const fetchComparisonResults = async () => {
      try {
        setIsLoading(true);
        setError("");
        const response = await apiHandler({
          url: `${endpoint.API_BASE_URL}${endpoint.GET_COMPARISON_RESULTS}?page=${currentPage}&limit=${rowsPerPage}`,
          method: "GET",
          authToken: { token },
          noCache: true,
        });

        const { rows, total, averages } = response?.data?.data || {};
        
        setComparisonData(rows || []);
        setAverageData(averages || {});
        setTotalRows(total || 0);
      } catch (err) {
        console.error("Error fetching comparison results:", err);
        setError("Failed to load comparison data. Please try again.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchComparisonResults();
  }, [currentPage, rowsPerPage, token]);


  const handleImageZoom = (image) => {
    if (!image) return;

    if (isMobile) {
      setZoomModal({ front: image, isOpen: true });
      return;
    }

    flushSync(() => {
      setZoomModal({ front: image, isOpen: false });
    });

    const modalElement = document.getElementById("comparisonZoomModal");
    if (modalElement) {
      BootstrapModal.getOrCreateInstance(modalElement).show();
    }
  };

  const closeZoomModal = () => {
    setZoomModal({ front: "", isOpen: false });
  };

  const handleCompareZoom = (index) => {
    setInitialCompareIndex(index);
  };

  // Pagination calculations
  const totalPages = Math.ceil(totalRows / rowsPerPage) || 1;
  const currentPageData = comparisonData;

  // Extract model metadata for KPI Stats Cards
  const models = comparisonData[0]?.models || [];

  return (
    <div className="app-page flex-1 flex flex-col min-h-0 min-w-0">
      
      {/* Title / Header */}
      <div className="px-6 pt-5 pb-4 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 shrink-0">
        <div>
          <h2 className="font-headline-md text-[20px] text-primary mb-0.5 font-bold tracking-tight">Comparison Table</h2>
          <p className="font-body-md text-[10px] text-secondary">Compare model inputs side-by-side alongside generated outputs and compute rendering metrics.</p>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 px-6 pt-1 pb-20 overflow-y-auto min-h-0">
        
        {isLoading && (
          <div className="py-20 text-center text-xs font-semibold text-secondary flex flex-col items-center justify-center gap-3">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
            <span>Loading comparison matrix...</span>
          </div>
        )}

        {error && (
          <div className="app-error-state py-8 px-4 text-center text-xs font-semibold flex items-center justify-center gap-2 rounded-xl mb-4">
            <MdInfoOutline size={18} />
            <span>{error}</span>
          </div>
        )}

        {!isLoading && !error && (
          <>
            {/* KPI Stats Grid */}
            <section className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
              
              {/* Card 1: Total Comparisons */}
              <div className="app-stat-card app-card app-card-hover status-completed min-h-[5rem]">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="app-stat-icon">
                    <MdCompare size={18} />
                  </div>
                  <div className="flex flex-col text-left">
                    <span className="app-stat-label leading-3">Total Comparisons</span>
                    <span className="app-stat-value leading-6 mt-0.5">
                      {totalRows}
                    </span>
                  </div>
                </div>
              </div>

              {/* Dynamic Cards 2-4: Model Render Speed Averages */}
              {models.slice(0, 3).map((model, idx) => (
                <div key={model.key || idx} className="app-stat-card app-card app-card-hover status-processing min-h-[5rem]">
                  <div className="flex items-center gap-2 sm:gap-3 min-w-0 w-full">
                    <div className="app-stat-icon">
                      <MdHourglassEmpty size={18} />
                    </div>
                    <div className="flex flex-col text-left min-w-0">
                      <span className="text-[9px] sm:text-[10px] text-secondary font-bold uppercase tracking-wider leading-3 mb-0.5">
                        Avg. Render Time
                      </span>
                      <span className="text-[10px] text-slate-500 font-semibold truncate leading-tight mb-0.5" title={model.title}>
                        {model.title}
                      </span>
                      <span className="app-stat-value leading-6 mt-0.5">
                        {averageData[model.key] ? `${averageData[model.key]}s` : "—"}
                      </span>
                    </div>
                  </div>
                </div>
              ))}

              {/* Render fallback placeholders if models.length is less than 3 */}
              {Array.from({ length: Math.max(0, 3 - models.length) }).map((_, i) => (
                <div key={`fallback-${i}`} className="app-stat-card app-card status-processing min-h-[5rem] opacity-50">
                  <div className="flex items-center gap-2 sm:gap-3">
                    <div className="app-stat-icon">
                      <MdHourglassEmpty size={18} />
                    </div>
                    <div className="flex flex-col text-left">
                      <span className="app-stat-label leading-3">No Model Loaded</span>
                      <span className="app-stat-value leading-6 mt-0.5">—</span>
                    </div>
                  </div>
                </div>
              ))}
            </section>

            {/* Table Matrix Container */}
            {currentPageData.length === 0 ? (
              <div className="app-panel app-empty-state py-16 flex flex-col items-center justify-center gap-3">
                <MdHourglassEmpty size={36} className="text-slate-300" />
                <span className="text-sm font-semibold text-secondary">No comparison data found</span>
              </div>
            ) : (
              <>
                <div className="app-panel overflow-hidden mb-4">
                  <div className="overflow-x-auto w-full -webkit-overflow-scrolling-touch">
                    <table className="w-full border-collapse text-left text-xs text-primary table-fixed min-w-[980px]">
                      <colgroup>
                        <col className="w-[8%]" />
                        <col className="w-[14%]" />
                        <col className="w-[14%]" />
                        {models.map((_, i) => (
                          <col key={`col-model-${i}`} className="w-[17%]" />
                        ))}
                        <col className="w-[13%]" />
                      </colgroup>
                      <thead>
                        <tr className="bg-slate-50 border-b border-slate-200/80 text-[10px] text-secondary font-bold uppercase tracking-wider">
                          <th className="px-4 py-4 text-center">S.No.</th>
                          <th className="px-4 py-4 text-center">Person</th>
                          <th className="px-4 py-4 text-center">Product</th>
                          {models.map((model, idx) => (
                            <th key={`header-model-${idx}`} className="px-4 py-4 text-center font-bold">
                              {model.title}
                            </th>
                          ))}
                          <th className="px-4 py-4 text-center">Action</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {currentPageData.map((row, index) => (
                          <tr key={row.sn || index} className="hover:bg-slate-50/30 transition-colors">
                            <td className="px-4 py-4 text-center font-semibold text-slate-500 align-middle">{row.sn}</td>
                            <td className="px-4 py-3 text-center align-middle">
                              <div className="flex justify-center">
                                <OptimizedImage
                                  src={row.ModelImage}
                                  alt={`Person ${row.personId}`}
                                  className="w-20 h-28 object-cover rounded-lg border border-slate-200 shadow-sm cursor-pointer transition-transform duration-200 hover:scale-105 hover:border-slate-400"
                                  onClick={() => handleImageZoom(row.ModelImage)}
                                  rootMargin="300px"
                                />
                              </div>
                            </td>
                            <td className="px-4 py-3 text-center align-middle">
                              <div className="flex justify-center">
                                <OptimizedImage
                                  src={row.productImage}
                                  alt={row.productName}
                                  className="w-20 h-28 object-cover rounded-lg border border-slate-200 shadow-sm cursor-pointer transition-transform duration-200 hover:scale-105 hover:border-slate-400"
                                  onClick={() => handleImageZoom(row.productImage)}
                                  rootMargin="300px"
                                />
                              </div>
                            </td>
                            {row.models?.map((model, modelIdx) => (
                              <td key={`${row.sn}-model-${modelIdx}`} className="px-4 py-3 text-center align-middle">
                                <div className="flex flex-col items-center gap-2">
                                  <OptimizedImage
                                    src={model.resultImage}
                                    alt={model.title}
                                    className="w-20 h-28 object-cover rounded-lg border border-slate-200 shadow-sm cursor-pointer transition-transform duration-200 hover:scale-105 hover:border-slate-400"
                                    onClick={() => handleImageZoom(model.resultImage)}
                                    rootMargin="300px"
                                  />
                                  {model.timeSeconds ? (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-slate-100 border border-slate-200 text-[10px] font-mono font-bold text-slate-600">
                                      {model.timeSeconds}s
                                    </span>
                                  ) : (
                                    <span className="text-[10px] text-slate-400">—</span>
                                  )}
                                </div>
                              </td>
                            ))}
                            <td className="px-4 py-4 text-center align-middle">
                              <button
                                className="app-button-primary font-bold text-xs px-3.5 py-2 rounded-lg cursor-pointer whitespace-nowrap"
                                onClick={() => handleCompareZoom(index)}
                                data-bs-toggle="modal"
                                data-bs-target="#compareZoomModal"
                              >
                                Compare
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="bg-slate-50/80 border-t border-slate-200 font-bold">
                          <td colSpan="3" className="px-6 py-4 text-right text-xs uppercase tracking-wider text-secondary font-bold align-middle">
                            Average Time
                          </td>
                          {models.map((model, idx) => (
                            <td key={`avg-time-${idx}`} className="px-4 py-4 text-center align-middle">
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full bg-slate-100 border border-slate-200/80 text-xs font-mono font-bold text-primary">
                                {averageData[model.key] ? `${averageData[model.key]}s` : "—"}
                              </span>
                            </td>
                          ))}
                          <td className="align-middle"></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>

                {/* Pagination Controls */}
                <footer
                  id="action-footer"
                  className="app-footer-bar fixed bottom-0 md:left-56 left-0 right-0 px-6 h-12 flex items-center justify-between z-50 transition-all duration-300"
                >
                  <InternalPagination
                    page={currentPage}
                    limit={rowsPerPage}
                    total={totalRows}
                    totalPages={totalPages}
                    limitOptions={[5, 10, 15, 20]}
                    onPageChange={(page) => setCurrentPage(page)}
                    onLimitChange={(limit) => {
                      setRowsPerPage(limit);
                      setCurrentPage(1);
                    }}
                    rowsLabel="Rows per page:"
                  />
                </footer>
              </>
            )}
          </>
        )}
      </div>

      <ZoomModal
        modalId="comparisonZoomModal"
        front={zoomModal.front}
        back=""
        onClose={closeZoomModal}
        showFlip={false}
      />
      <MobileZoomModal
        isOpen={zoomModal.isOpen}
        front={zoomModal.front}
        back=""
        onClose={closeZoomModal}
        showFlip={false}
      />
      <CompareZoomModal
        modalId="compareZoomModal"
        items={comparisonData}
        initialIndex={initialCompareIndex}
        totalItems={totalRows}
        onZoom={handleImageZoom}
      />
    </div>
  );
};

export default ComparisonTable;
